import React, { useState, useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import imageCompression from 'browser-image-compression';
import {
  Mic,
  Send,
  AlertTriangle,
  ChevronRight,
  CheckCircle2,
  StopCircle,
  ChevronLeft,
  Edit3,
  AlertCircle,
  Info,
  Loader2,
  Camera,
  X,
  ImageIcon,
  Link as LinkIcon,
  Copy,
  Check,
  ExternalLink
} from 'lucide-react';
import { analyzeSafetyReport, transcribeAudio, getLastDebugInfo } from '../services/aiService';
// RiskLevel / Category は enum なので値としても使う（写真のみ報告の既定値を組み立てるため）
import { RiskLevel, Category } from '../types';
import { riskLabel } from '../utils/caseLabels';
import { splitLessonByLanguage } from '../utils/caseClassification';
import type { AIAnalysisResult, SubmissionData, PostInput } from '../types';
import { logger } from '../utils/logger';
import { logError } from '../services/clientLogger';

// Sanitize text content to prevent XSS
const sanitizeText = (text: string): string => {
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

// ============================================================================
// 報告下書き（sessionStorage）
// ============================================================================
// 目的: リロード・タブの自動再読込・QRコンテキスト喪失で WorkerInput が
// 再マウントされても、入力テキストを失わないための保険。
// 「戻る」やDB保存失敗からの復元は App.tsx の submissionData（メモリ内）が担い、
// こちらはあくまでページ再読込に対する保険という役割分担。
//
// 匿名性: 下書きは匿名化前の生テキストなので localStorage には置かない。
// sessionStorage（タブ単位・タブを閉じれば消える）+ TTL 30分 +
// 送信成功/明示破棄時のクリアで、共有端末で次の人に見える窓を最小化する。
// Blob（音声・写真）は保存できないため「あったこと」だけフラグで残し、
// 復元時に「写真・音声は復元できない」と正直に伝える。
const REPORT_DRAFT_KEY = 'genbasafeai_report_draft_v1';
const REPORT_DRAFT_TTL_MS = 30 * 60 * 1000; // 30分
const REPORT_DRAFT_MAX_TEXT = 2000; // 入力欄の maxLength と同値

interface ReportDraft {
  text: string;
  hadAudio: boolean;
  hadPhoto: boolean;
  savedAt: number; // epoch ms
}

function readReportDraft(): ReportDraft | null {
  try {
    const raw = sessionStorage.getItem(REPORT_DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as ReportDraft;
    if (typeof draft?.text !== 'string' || typeof draft?.savedAt !== 'number') return null;
    if (Date.now() - draft.savedAt > REPORT_DRAFT_TTL_MS) {
      sessionStorage.removeItem(REPORT_DRAFT_KEY);
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

function saveReportDraft(draft: Omit<ReportDraft, 'savedAt'>): void {
  try {
    if (!draft.text.trim() && !draft.hadAudio && !draft.hadPhoto) {
      // 全部空なら下書き自体を消す（空下書きを残さない）
      sessionStorage.removeItem(REPORT_DRAFT_KEY);
      return;
    }
    sessionStorage.setItem(
      REPORT_DRAFT_KEY,
      JSON.stringify({
        ...draft,
        text: draft.text.slice(0, REPORT_DRAFT_MAX_TEXT),
        savedAt: Date.now()
      })
    );
  } catch {
    // ストレージが使えない環境（プライベートブラウジング等）では黙って諦める。
    // 下書きは保険であり、無くても報告フローは成立する。
  }
}

/** 送信成功時・明示破棄時に App.tsx からも呼ぶ */
export function clearReportDraft(): void {
  try {
    sessionStorage.removeItem(REPORT_DRAFT_KEY);
  } catch {
    // ignore
  }
}

// ============================================================================
// InputErrorCard - alert() の置き換え（現場のスマホで読めるインラインエラー）
// ============================================================================
// 「何が起きたか」+「次に何をすればいいか」を必ずセットで出す。
// 技術情報（英語メッセージ・エラーコード）は出さない（logError に残す）。
//
// トーストにしない理由: トーストは画面上部に数秒で消える。手袋・屋外・直射日光の
// 現場では見逃す前提で設計する。エラーは閉じるか再試行するまでその場に残す。
export interface InputErrorState {
  title: string;  // 何が起きたか
  action: string; // 次にどうすればよいか
}

const InputErrorCard: React.FC<{ error: InputErrorState; onClose: () => void }> = ({ error, onClose }) => (
  <div role="alert" className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl mb-4">
    <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
    <div className="flex-1">
      <p className="text-sm font-bold text-red-800">{error.title}</p>
      <p className="text-xs text-red-700 mt-0.5 leading-relaxed">{error.action}</p>
    </div>
    <button
      type="button"
      onClick={onClose}
      aria-label="エラー表示を閉じる"
      className="shrink-0 p-1 text-red-400 hover:text-red-600 transition"
    >
      <X size={14} />
    </button>
  </div>
);

// ============================================================================
// WorkerHome - QR Landing Screen
// ============================================================================
interface WorkerHomeProps {
  onStart: () => void;
  onBack: () => void;
  teamName?: string;
  stats?: {
    monthPosts: number;
    resolvedCount: number;
    ranking?: number;
    streak?: number;
  };
  organizationId?: string;
  qrToken?: string;
  /** 自分の投稿一覧 (/my-reports) への遷移. localStorage に履歴がある時のみ表示する想定. */
  onShowMyReports?: () => void;
}

const MY_REPORTS_KEY = 'genbasafeai_my_reports';

function countMyReports(): number {
  try {
    const raw = localStorage.getItem(MY_REPORTS_KEY);
    if (!raw) return 0;
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list.length : 0;
  } catch {
    return 0;
  }
}

export const WorkerHome: React.FC<WorkerHomeProps> = ({
  onStart,
  onBack,
  teamName = '東京第3工場 Bチーム',
  stats = { monthPosts: 12, resolvedCount: 8 } as WorkerHomeProps['stats'],
  organizationId,
  qrToken,
  onShowMyReports,
}) => {
  const [myReportsCount, setMyReportsCount] = useState<number>(0);
  useEffect(() => {
    setMyReportsCount(countMyReports());
  }, []);
  return (
    <div className="flex flex-col h-full justify-between py-4 animate-fade-in relative">
      <button
        onClick={onBack}
        className="absolute -top-2 -left-2 p-2 text-slate-500 hover:text-slate-700 transition flex items-center text-sm font-medium z-10"
      >
        <ChevronLeft size={18} className="mr-1" />
        ホーム
      </button>

      <div className="text-center mb-6 mt-8">
        <h2 className="text-2xl font-bold text-industrial-900 mb-2">{teamName}</h2>
        <p className="text-slate-500 text-sm">安全活動にご協力ありがとうございます。</p>
      </div>

      {/* 過去投稿の確認 (リピーター動線・最上位 CTA). カード型で目立たせる. 1件以上ある時のみ表示. */}
      {onShowMyReports && myReportsCount > 0 && (
        <button
          type="button"
          onClick={onShowMyReports}
          className="w-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl p-4 mb-6 shadow-lg shadow-blue-500/30 hover:from-blue-700 hover:to-indigo-800 active:scale-[0.98] transition flex items-center gap-3 text-left"
        >
          <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
            📋
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-blue-100 mb-0.5">My Reports</p>
            <p className="font-bold text-base leading-tight">
              これまでの投稿 <span className="text-yellow-300">{myReportsCount}</span> 件を確認する
            </p>
            <p className="text-xs text-blue-100 mt-0.5">対応経過・解決状況をチェック</p>
          </div>
          <span className="flex-shrink-0 text-white" aria-hidden>›</span>
        </button>
      )}

      <div className="bg-orange-50 border border-orange-100 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-orange-800 flex items-center">
            <AlertTriangle size={18} className="mr-2" />
            今月の安全状況
          </h3>
          {stats.ranking && (
            <span className="text-xs font-bold text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
              ランキング {stats.ranking}位
            </span>
          )}
        </div>
        <div className="flex justify-around text-center">
          <div>
            <div className="text-2xl font-bold text-industrial-900">
              {stats.monthPosts}
              <span className="text-sm font-normal text-slate-500 ml-1">件</span>
            </div>
            {/* 値は get_org_monthly_case_stats（当月・JST基準）。
                「総報告数」だと累計と読めて数値と食い違うため当月と明示する。 */}
            <div className="text-xs text-slate-600 mt-1">今月の報告</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-emerald-600">
              {stats.resolvedCount}
              <span className="text-sm font-normal text-slate-500 ml-1">件</span>
            </div>
            <div className="text-xs text-slate-600 mt-1">今月の改善</div>
          </div>
          {stats.streak && (
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {stats.streak}
                <span className="text-sm font-normal text-slate-500 ml-1">日</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">連続投稿</div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <button
          onClick={onStart}
          className="w-full bg-safety-600 hover:bg-safety-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg shadow-orange-200 transform transition active:scale-95 flex items-center justify-center text-lg"
        >
          <Send className="mr-2" />
          報告・提案をする
        </button>
        <p className="text-center text-xs text-slate-600">
          ※個人は特定されません（AIが自動匿名化）
        </p>
      </div>

    </div>
  );
};

// ============================================================================
// ============================================================================
// WorkerInput - Report Input Screen
// ============================================================================
interface WorkerInputProps {
  onSubmit: (data: SubmissionData) => void;
  onCancel: () => void;
  organizationId?: string;
  equipmentInfo?: string; // 設備情報（AIコンテキスト用）
  /**
   * 確認画面の「戻る」やAI分析不完全時の差し戻しで再マウントされたときの復元用。
   * App.tsx が保持している submissionData.input をそのまま渡す。
   * メモリ内の Blob（音声・写真）も含めて完全に復元できる。
   */
  initialInput?: PostInput;
  /**
   * 読み取ったQRのトークン。Edge Function 側の認可に使う。
   * 作業員はセッションを持たないため、これが無いと AI分析が 401 になり
   * モック応答に落ちる（2026-08-20 に本番で発生）。
   */
  qrToken?: string;
}

export const WorkerInput: React.FC<WorkerInputProps> = ({ onSubmit, onCancel, organizationId: propOrgId, equipmentInfo: propEquipmentInfo, initialInput, qrToken: propQrToken }) => {
  // Get organizationId from props or try sessionStorage as fallback
  const [organizationId, setOrganizationId] = useState<string | undefined>(propOrgId);
  /* organizationId と同じ理由でフォールバックを持つ。途中でリロードすると
     prop が失われるが、トークンだけ欠けると認可が通らず AI分析が静かに
     モックへ落ちる。復元元は App.tsx が書いている同じキー。 */
  const [qrToken, setQrToken] = useState<string | undefined>(
    () => propQrToken || sessionStorage.getItem('genbasafeai_qr_token') || undefined,
  );
  useEffect(() => {
    if (propQrToken && propQrToken !== qrToken) setQrToken(propQrToken);
  }, [propQrToken, qrToken]);
  const [equipmentInfo, setEquipmentInfo] = useState<string | undefined>(propEquipmentInfo);

  // Try to recover organizationId and equipmentInfo from sessionStorage if missing
  useEffect(() => {
    if (!organizationId || !equipmentInfo) {
      try {
        const storedContext = sessionStorage.getItem('genbasafeai_qr_context');
        if (storedContext) {
          const parsedContext = JSON.parse(storedContext);
          if (!organizationId && parsedContext.organizationId) {
            logger.log('WorkerInput: Recovered organizationId from sessionStorage');
            setOrganizationId(parsedContext.organizationId);
          }
          if (!equipmentInfo && parsedContext.equipmentInfo) {
            logger.log('WorkerInput: Recovered equipmentInfo from sessionStorage');
            setEquipmentInfo(parsedContext.equipmentInfo);
          }
        }
      } catch (e) {
        logger.warn('WorkerInput: Failed to recover context from sessionStorage:', e);
      }
    }
  }, [organizationId, equipmentInfo]);

  // 入力手段（テキスト/音声/写真）の選択。デモ画面と同じ横並びタブUI。
  // 選択中の手段のみ表示してスクロールを抑える（text/audio/image の各stateは保持されるので複数添付も可能）。
  // --- 入力の復元 ---
  // 優先順位: initialInput（「戻る」等。メモリ内で Blob 含め完全）
  //         > sessionStorage 下書き（リロード等。テキストのみ）
  //         > 空
  // useRef で初回マウント時に一度だけ読む（再レンダーで下書きを読み直さない）。
  const draftRef = useRef<ReportDraft | null>(initialInput ? null : readReportDraft());

  // 復元時は「中身が入っているタブ」を初期表示する（復元されたことが一目で分かるように）。
  const [method, setMethod] = useState<'text' | 'voice' | 'photo'>(() => {
    if (initialInput) {
      if (initialInput.text.trim()) return 'text';
      if (initialInput.audioBlob) return 'voice';
      if (initialInput.imageBlob) return 'photo';
    }
    return 'text';
  });

  // Text input state
  const [text, setText] = useState(() => initialInput?.text ?? draftRef.current?.text ?? '');

  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(initialInput?.audioBlob ?? null);
  const [hasAudio, setHasAudio] = useState(!!initialInput?.audioBlob);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  // Image state
  // プレビューURLは initialInput.imagePreview を使わず Blob から作り直す。
  // 元の object URL は removeImage で revoke 済みの可能性があり、信用できないため。
  const [imageBlob, setImageBlob] = useState<Blob | null>(initialInput?.imageBlob ?? null);
  const [imagePreview, setImagePreview] = useState<string | null>(() =>
    initialInput?.imageBlob ? URL.createObjectURL(initialInput.imageBlob) : null
  );
  const [isCompressingImage, setIsCompressingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // リロード後に下書きから復元した場合の通知。
  // 写真・音声は復元できないので、あったのに消えた場合は必ず正直に伝える（嘘をつかない）。
  const [restoreNotice] = useState<string | null>(() => {
    if (initialInput || !draftRef.current?.text) return null;
    if (draftRef.current.hadAudio || draftRef.current.hadPhoto) {
      return '入力途中のテキストを復元しました。写真・音声は復元できないため、必要ならもう一度添付してください。';
    }
    return '入力途中のテキストを復元しました。';
  });

  // インラインエラー（alert の置き換え）。次の操作を始めたら自動で消す。
  const [inputError, setInputError] = useState<InputErrorState | null>(null);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearInterval(timerRef.current);
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // 下書きの自動保存（500ms デバウンス）。Blob は保存できないので有無フラグのみ。
  useEffect(() => {
    const t = window.setTimeout(() => {
      saveReportDraft({ text, hadAudio: hasAudio, hadPhoto: !!imageBlob });
    }, 500);
    return () => window.clearTimeout(t);
  }, [text, hasAudio, imageBlob]);

  // 「戻る」（ホームへ）＝ 明示的な破棄。
  // 共有端末で次の人に下書きが見えないよう、ここで必ず消す。
  const handleCancel = () => {
    clearReportDraft();
    onCancel();
  };

  // Recording timer
  useEffect(() => {
    if (isRecording) {
      timerRef.current = window.setInterval(() => {
        setRecordingTime((prev) => {
          // Max 60 seconds
          if (prev >= 60) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (timerRef.current !== null) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current !== null) clearInterval(timerRef.current);
    };
  }, [isRecording]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Start audio recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4'
      });

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType
        });
        setAudioBlob(blob);
        setHasAudio(true);

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
    } catch (error) {
      logger.error('Failed to start recording:', error);
      logError('WorkerInput: mic access failed', {
        reason: error instanceof Error ? error.message : String(error)
      }, 'WorkerFlow');
      setInputError({
        title: 'マイクが使えません',
        action: 'スマホの設定でこのサイトのマイクを許可してください。急ぐ場合はテキスト入力でも報告できます。'
      });
    }
  };

  // Stop audio recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  // Toggle recording
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      // Clear previous recording
      setAudioBlob(null);
      setHasAudio(false);
      startRecording();
    }
  };

  // Image compression options
  const imageCompressionOptions = {
    maxSizeMB: 1, // Maximum 1MB
    maxWidthOrHeight: 1920, // Max dimension
    useWebWorker: true,
    fileType: 'image/jpeg' as const,
    initialQuality: 0.8
  };

  // Handle image selection
  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setInputError({
        title: 'このファイルは添付できません',
        action: '写真（画像ファイル）を選び直してください。'
      });
      return;
    }

    setIsCompressingImage(true);

    try {
      logger.log('handleImageSelect: Starting compression', { originalSize: file.size, type: file.type });

      // Compress image
      const compressedFile = await imageCompression(file, imageCompressionOptions);
      logger.log('handleImageSelect: Compression complete', { compressedSize: compressedFile.size });

      // Create blob
      const blob = new Blob([compressedFile], { type: 'image/jpeg' });
      setImageBlob(blob);

      // Create preview URL
      const previewUrl = URL.createObjectURL(blob);
      setImagePreview(previewUrl);
    } catch (error) {
      logger.error('handleImageSelect: Compression failed', error);
      setInputError({
        title: 'この写真を読み込めませんでした',
        action: 'もう一度撮影するか、別の写真をお試しください。'
      });
    } finally {
      setIsCompressingImage(false);
      // Reset input value to allow selecting same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove selected image
  const removeImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageBlob(null);
    setImagePreview(null);
  };

  // 送信可否: テキスト / 音声 / 写真 のいずれか1つでも入力されていればよい。
  // 「写真だけ」を弾いていたため、写真タブで撮っても「次へ」が押せない不具合があった。
  // 現場では「とりあえず撮って送る」が最も自然な報告手段なので、写真単独を許可する。
  const canSubmit = !!text.trim() || hasAudio || !!imageBlob;

  // Submit handler
  const handleSubmit = async () => {
    if (!canSubmit) return;

    logger.log('handleSubmit: START');

    // デバウンス中に「次へ」を押した場合の取りこぼし防止（最新テキストを下書きに反映）
    saveReportDraft({ text, hadAudio: hasAudio, hadPhoto: !!imageBlob });

    setInputError(null);
    setIsProcessing(true);
    let transcribedText: string | undefined;

    // Get organizationId and equipmentInfo - check state first, then sessionStorage as fallback
    let orgId = organizationId;
    let eqInfo = equipmentInfo;

    if (!orgId || !eqInfo) {
      try {
        const storedContext = sessionStorage.getItem('genbasafeai_qr_context');
        if (storedContext) {
          const parsedContext = JSON.parse(storedContext);
          if (!orgId && parsedContext.organizationId) {
            orgId = parsedContext.organizationId;
            logger.log('handleSubmit: Recovered organizationId from sessionStorage:', orgId);
            // Also update state for future use
            setOrganizationId(orgId);
          }
          if (!eqInfo && parsedContext.equipmentInfo) {
            eqInfo = parsedContext.equipmentInfo;
            logger.log('handleSubmit: Recovered equipmentInfo from sessionStorage');
            setEquipmentInfo(eqInfo);
          }
        }
      } catch (e) {
        logger.warn('handleSubmit: Failed to recover context from sessionStorage:', e);
      }
    }

    if (!orgId) {
      orgId = 'demo';
      logger.log('handleSubmit: No organizationId, running in demo mode');
    }

    logger.log('handleSubmit: Using organizationId:', orgId);

    try {
      // Step 1: Transcribe audio if present
      if (audioBlob) {
        setProcessingStep('音声を文字起こし中...');
        const transcription = await transcribeAudio(audioBlob, orgId, qrToken);
        if (transcription.success && transcription.text) {
          transcribedText = transcription.text;
        } else {
          // Transcription failed - show appropriate error message
          const errorMsg = transcription.error || '音声の文字起こしに失敗しました';
          logger.error('Transcription failed:', errorMsg);

          // Check if it's a usage limit error
          logError('WorkerInput: transcription failed', { reason: errorMsg }, 'WorkerFlow');
          if (errorMsg.includes('Usage limit') || errorMsg.includes('LIMIT_EXCEEDED')) {
            // 旧文言は「管理者画面のプラン設定から」と案内していたが、
            // 匿名の作業員には実行できない。いま報告する手段を伝える。
            setInputError({
              title: '音声入力が今月の上限に達しました',
              action: 'テキストまたは写真での報告をお使いください（報告はこれまで通り送信できます）。'
            });
          } else {
            setInputError({
              title: '音声を文字にできませんでした',
              action: '録音した音声は残っています。電波の良い場所でもう一度「次へ」を押すか、テキストで入力してください。'
            });
          }
          setIsProcessing(false);
          setProcessingStep('');
          return;
        }
      }

      // Ensure we have content to analyze
      const hasContent = text.trim() || transcribedText;
      if (!hasContent) {
        // 写真だけの報告。
        // AI 分析 (genbasafeai_analyze-report) はテキストしか受け取れず画像は見られないので、
        // ここで AI を呼んでも中身のない文章を推測で生成するだけになる（＝現場に嘘を届ける）。
        // したがって AI は呼ばず（月次クォータも消費しない）、
        // 「写真が本体である」ことが管理者に伝わる定型に落とす。本文は次の確認画面で編集できる。
        if (imageBlob) {
          logger.log('handleSubmit: photo-only report, skipping AI analysis');
          onSubmit({
            input: {
              text,
              audioBlob: audioBlob || undefined,
              transcribedText,
              imageBlob,
              imagePreview: imagePreview || undefined
            },
            analysis: {
              title: '写真による報告',
              // 危険度・カテゴリは推定できないため既定値。管理者が写真を見て判断する前提。
              formattedBody: '【状況】写真のみの報告です。詳細は添付写真をご確認ください。',
              riskLevel: RiskLevel.MEDIUM,
              category1: Category.METHOD,
              category2: '写真報告',
              lesson: ''
            },
            requiresConfirmation: true
          });
          return;
        }

        setInputError({
          title: '報告内容が入力されていません',
          action: 'テキスト・音声・写真のいずれか1つを入力してください。'
        });
        setIsProcessing(false);
        setProcessingStep('');
        return;
      }

      // Step 2: AI Analysis
      setProcessingStep('AIが内容を分析中...');
      logger.log('handleSubmit: Calling analyzeSafetyReport with:', { textLen: text.length, orgId, hasEquipmentInfo: !!eqInfo });

      const analysis = await analyzeSafetyReport(
        text,
        transcribedText,
        orgId,
        eqInfo, // 設備情報をAI分析に渡す
        qrToken // 匿名の作業員の認可。渡さないと Edge Function が 401 を返す
      );

      logger.log('handleSubmit: AI analysis result:', analysis);

      // Determine if confirmation is required (only for audio input now)
      const requiresConfirmation = hasAudio;

      onSubmit({
        input: {
          text,
          audioBlob: audioBlob || undefined,
          transcribedText,
          imageBlob: imageBlob || undefined,
          imagePreview: imagePreview || undefined
        },
        analysis,
        requiresConfirmation
      });
    } catch (error) {
      logger.error('Submission error:', error);
      logError('WorkerInput: submit failed', {
        reason: error instanceof Error ? error.message : String(error)
      }, 'WorkerFlow');
      setInputError({
        title: '送信の準備ができませんでした',
        action: '入力した内容は消えていません。電波の良い場所で、もう一度「次へ」を押してください。'
      });
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  // Processing screen
  if (isProcessing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 animate-fade-in px-4">
        <Loader2 className="w-16 h-16 text-safety-600 animate-spin mb-4" />
        <p className="text-industrial-900 font-medium">{processingStep || 'AIが分析中...'}</p>
        <p className="text-sm text-slate-500 mt-2">
          個人情報の匿名化処理を行っています
        </p>
        <p className="text-xs text-slate-500 mt-1">この処理は数秒で完了します</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Step Indicator */}
      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center text-sm">
          <span className="w-6 h-6 rounded-full bg-safety-600 text-white flex items-center justify-center text-xs font-bold">
            1
          </span>
          <span className="ml-2 font-medium text-safety-600">入力</span>
          <div className="w-8 h-0.5 bg-slate-200 mx-2" />
          <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-400 flex items-center justify-center text-xs font-bold">
            2
          </span>
          <span className="ml-2 text-slate-400">完了</span>
        </div>
      </div>

      <h3 className="font-bold text-lg text-industrial-900 mb-4">報告内容の入力</h3>

      {/* リロード後の下書き復元通知（写真・音声が消えた場合はその旨を明示） */}
      {restoreNotice && (
        <div role="status" className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-xl mb-4">
          <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800 leading-relaxed">{restoreNotice}</p>
        </div>
      )}

      {inputError && <InputErrorCard error={inputError} onClose={() => setInputError(null)} />}

      {/* 入力手段の横並びタブ（テキスト/音声/写真）。選んだ手段だけ下に表示。
          入力済みの手段には右上にドットを表示するので、切り替えても複数添付できることが分かる。 */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {([
          { key: 'text' as const, emoji: '✏️', title: 'テキスト', sub: '入力して報告', has: !!text.trim() },
          { key: 'voice' as const, emoji: '🎤', title: '音声', sub: '話して報告', has: hasAudio },
          { key: 'photo' as const, emoji: '📷', title: '写真', sub: '撮って報告', has: !!imagePreview },
        ]).map((m) => (
          <button
            key={m.key}
            onClick={() => setMethod(m.key)}
            className={`relative flex flex-col items-center justify-center gap-0.5 py-3 px-1.5 rounded-xl border transition ${
              method === m.key
                ? 'bg-orange-50 border-safety-500 ring-1 ring-safety-200'
                : 'bg-white border-slate-200 hover:bg-slate-50'
            }`}
          >
            <span className="text-xl leading-none">{m.emoji}</span>
            <span className="text-[13px] font-bold text-industrial-900">{m.title}</span>
            <span className="text-[10px] text-slate-600">{m.sub}</span>
            {m.has && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-safety-500" aria-label="入力あり" />
            )}
          </button>
        ))}
      </div>

      {/* Text Input */}
      {method === 'text' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            気付いたこと・ヒヤリとしたこと <span className="text-red-500">*</span>
          </label>
          <textarea
            className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-safety-500 focus:border-transparent min-h-[120px] text-base transition-all placeholder-slate-400"
            placeholder="例）3階の足場で作業中、手すりが少しぐらついているのに気づいた。"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={2000}
          />
          <p className="text-xs text-slate-500 mt-1 text-right">{text.length}/2000</p>
        </div>
      )}

      {/* Audio Recording Button */}
      {method === 'voice' && (
        <div className="mb-4">
          <button
            className={`w-full p-6 border rounded-xl flex flex-col items-center justify-center transition-all duration-300 relative overflow-hidden ${
              isRecording
                ? 'bg-red-50 border-red-500 text-red-600 ring-2 ring-red-200'
                : hasAudio
                ? 'bg-emerald-50 border-emerald-500 text-emerald-600 ring-1 ring-emerald-200'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
            onClick={toggleRecording}
          >
            {isRecording ? (
              <>
                <div className="absolute top-2 right-2 flex items-center space-x-1">
                  <span className="w-2 h-2 bg-red-600 rounded-full animate-ping" />
                </div>
                <StopCircle size={28} className="mb-2 animate-pulse" />
                <span className="text-lg font-mono font-bold">
                  {formatTime(recordingTime)}
                </span>
                <span className="text-xs font-medium mt-1">タップで停止（最長60秒で自動停止）</span>
              </>
            ) : hasAudio ? (
              <>
                <CheckCircle2 size={28} className="mb-2" />
                <span className="text-xs font-bold">録音完了</span>
                <span className="text-[10px]">タップで再録音</span>
              </>
            ) : (
              <>
                <Mic size={28} className="mb-2 opacity-70" />
                <span className="text-xs font-bold">音声入力</span>
                <span className="text-[10px]">タップして録音開始（最長60秒）</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Photo Input Section */}
      {method === 'photo' && (
        <div className="mb-4">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
          />

          {imagePreview ? (
            /* Image Preview */
            <div className="relative">
              <img
                src={imagePreview}
                alt="プレビュー"
                className="w-full h-48 object-cover rounded-xl border border-slate-200"
              />
              <button
                onClick={removeImage}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition"
                aria-label="画像を削除"
              >
                <X size={16} />
              </button>
              <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                <ImageIcon size={12} className="inline mr-1" />
                写真添付済み
              </div>
            </div>
          ) : null}

          {/* ひとこと補足: 写真だけでも送れるが、一言あるとAIが状況を整理できる。
              報告本文は1つなので、テキストタブと同じ text state を共有する。 */}
          {imagePreview && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                ひとこと補足 <span className="text-slate-500 font-normal">（任意）</span>
              </label>
              <textarea
                className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-safety-500 focus:border-transparent min-h-[64px] text-base transition-all placeholder-slate-400"
                placeholder="例）3階の手すりがぐらついています"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={2000}
              />
              <p className="text-xs text-slate-500 mt-1">
                写真だけでも送信できます。ひとこと添えるとAIが状況を整理します。
              </p>
            </div>
          )}

          {!imagePreview && (
            /* Upload Button */
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isCompressingImage}
              className={`w-full p-6 border border-dashed rounded-xl flex flex-col items-center justify-center transition-all ${
                isCompressingImage
                  ? 'bg-slate-50 border-slate-300 text-slate-400 cursor-wait'
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-slate-400'
              }`}
            >
              {isCompressingImage ? (
                <>
                  <Loader2 size={28} className="mb-2 animate-spin" />
                  <span className="text-xs font-bold">画像を処理中...</span>
                </>
              ) : (
                <>
                  <Camera size={28} className="mb-2 opacity-70" />
                  <span className="text-xs font-bold">写真を追加</span>
                  <span className="text-[10px]">撮影またはギャラリーから選択</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          onClick={handleCancel}
          className="flex-1 py-3 px-4 border border-slate-300 rounded-xl text-slate-600 font-medium hover:bg-slate-50 transition"
        >
          戻る
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`flex-1 py-3 px-4 rounded-xl text-white font-bold shadow-md flex items-center justify-center transition-all active:scale-95 ${
            !canSubmit
              ? 'bg-slate-300 cursor-not-allowed'
              : 'bg-safety-600 hover:bg-safety-700'
          }`}
        >
          次へ
          <ChevronRight size={18} className="ml-1" />
        </button>
      </div>

    </div>
  );
};

// ============================================================================
// WorkerConfirm - AI Result Confirmation Screen
// ============================================================================
interface WorkerConfirmProps {
  data: SubmissionData;
  /**
   * 送信の最終確定。成功なら true、失敗なら false を返す。
   * 失敗時はこの画面に留まり、同じ「送信する」ボタンでそのまま再試行できる
   * （worker_form に戻すと AI 分析の再実行＝クォータ二重消費になるため）。
   */
  onConfirm: (analysis: AIAnalysisResult) => Promise<boolean>;
  onBack: () => void;
  hasAudioInput: boolean;
}

export const WorkerConfirm: React.FC<WorkerConfirmProps> = ({
  data,
  onConfirm,
  onBack,
  hasAudioInput,
}) => {
  // Get AI debug info for limit exceeded warning
  const aiDebug = getLastDebugInfo();

  // Sanitize AI analysis results on init
  const [editedAnalysis, setEditedAnalysis] = useState<AIAnalysisResult>(() => ({
    ...data.analysis,
    title: sanitizeText(data.analysis.title),
    formattedBody: sanitizeText(data.analysis.formattedBody),
    lesson: sanitizeText(data.analysis.lesson),
    category2: sanitizeText(data.analysis.category2)
  }));
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<InputErrorState | null>(null);

  const handleConfirmClick = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(null);
    const ok = await onConfirm(editedAnalysis);
    // 成功時は worker_success へ遷移してこの画面ごと消えるので何もしない。
    // 失敗時だけボタンを復活させ、その場で再試行できるようにする。
    if (!ok) {
      setIsSubmitting(false);
      setSubmitError({
        title: '報告を送信できませんでした',
        action: '入力した内容は消えていません。電波の良い場所で、もう一度「送信する」を押してください。何度も失敗する場合は、時間をおいて試すか、現場責任者に直接お知らせください。'
      });
    }
  }, [isSubmitting, onConfirm, editedAnalysis]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedAnalysis((prev) => ({ ...prev, title: sanitizeText(e.target.value) }));
  };

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedAnalysis((prev) => ({ ...prev, formattedBody: sanitizeText(e.target.value) }));
  };

  const getRiskBadgeColor = (level: RiskLevel) => {
    switch (level) {
      case 'High':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Step Indicator */}
      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center text-sm">
          <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">
            <CheckCircle2 size={14} />
          </span>
          <span className="ml-2 text-slate-500">入力</span>
          <div className="w-8 h-0.5 bg-safety-600 mx-2" />
          <span className="w-6 h-6 rounded-full bg-safety-600 text-white flex items-center justify-center text-xs font-bold">
            2
          </span>
          <span className="ml-2 font-medium text-safety-600">確認</span>
        </div>
      </div>

      <h3 className="font-bold text-lg text-industrial-900 mb-2">
        AIが整形した内容を確認
      </h3>

      {/* Audio Warning */}
      {hasAudioInput && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
          <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            音声入力は自動変換のため、誤認識の可能性があります。
            内容をご確認のうえ送信してください。
          </p>
        </div>
      )}

      {/* Title */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">
          タイトル
        </label>
        {isEditing ? (
          <input
            type="text"
            value={editedAnalysis.title}
            onChange={handleTitleChange}
            className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent"
            maxLength={30}
          />
        ) : (
          <div className="p-3 bg-slate-50 rounded-lg text-industrial-900 font-medium">
            {editedAnalysis.title}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">
          整形された本文
        </label>
        {isEditing ? (
          <textarea
            value={editedAnalysis.formattedBody}
            onChange={handleBodyChange}
            maxLength={2000}
            className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent min-h-[150px]"
          />
        ) : (
          <div className="p-3 bg-slate-50 rounded-lg text-slate-700 whitespace-pre-wrap text-sm">
            {editedAnalysis.formattedBody}
          </div>
        )}
      </div>

      {/* Categories & Risk - Read Only */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            危険度
          </label>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getRiskBadgeColor(
              editedAnalysis.riskLevel
            )}`}
          >
            {riskLabel(editedAnalysis.riskLevel)}
          </span>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            カテゴリ
          </label>
          <span className="text-sm text-slate-700">{editedAnalysis.category1}</span>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-xs font-medium text-slate-500 mb-1">
          2次カテゴリ
        </label>
        <span className="inline-flex items-center px-2 py-1 bg-slate-100 rounded text-sm text-slate-600">
          #{editedAnalysis.category2}
        </span>
      </div>

      {/* Lesson
          非日本語の報告では「原言語版 →【日本語訳】→ 日本語版」が
          併記されている。この画面を見るのは報告した本人なので、
          自分の言語の版だけを出す（日本語版は /my-report で確認できる）。
          日本語の報告では併記が無く、従来どおり全文が出る。 */}
      {editedAnalysis.lesson && (
        <div className="mb-6">
          <label className="block text-xs font-medium text-slate-500 mb-1">
            教訓
          </label>
          <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800 whitespace-pre-line">
            {(() => {
              const { native, japanese } = splitLessonByLanguage(editedAnalysis.lesson);
              return native ?? japanese;
            })()}
          </div>
        </div>
      )}

      {/* Edit Toggle */}
      <button
        onClick={() => setIsEditing(!isEditing)}
        className="flex items-center text-sm text-safety-600 hover:text-safety-700 mb-4"
      >
        <Edit3 size={14} className="mr-1" />
        {isEditing ? '編集を終了' : '内容を修正する'}
      </button>

      {/* LIMIT_EXCEEDED Alert - Prominent Warning */}
      {aiDebug.limitExceeded && (
        <div className="mb-4 p-4 bg-red-100 border-2 border-red-400 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 font-bold mb-2">
            <AlertTriangle size={20} />
            AI分析利用上限に達しました
          </div>
          <p className="text-sm text-red-600">
            今月のAI分析回数の上限に達しています。
            管理者に連絡するか、プランのアップグレードをご検討ください。
          </p>
          <p className="text-xs text-red-500 mt-2">
            ※ 報告は送信されますが、AI整形機能は制限されています
          </p>
        </div>
      )}

      {/* 送信失敗のインラインエラー（トーストは見逃されるので、ボタンの直上に常設する） */}
      {submitError && <InputErrorCard error={submitError} onClose={() => setSubmitError(null)} />}

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          onClick={onBack}
          disabled={isSubmitting}
          className="flex-1 py-3 px-4 border border-slate-300 rounded-xl text-slate-600 font-medium hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          戻る
        </button>
        <button
          onClick={handleConfirmClick}
          disabled={isSubmitting}
          className={`flex-1 py-3 px-4 rounded-xl text-white font-bold shadow-md flex items-center justify-center transition-all ${
            isSubmitting
              ? 'bg-slate-400 cursor-not-allowed'
              : 'bg-safety-600 hover:bg-safety-700 active:scale-95'
          }`}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              送信中...
            </>
          ) : (
            <>
              送信する
              <Send size={16} className="ml-2" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// WorkerSuccess - Submission Complete Screen
// ============================================================================
interface WorkerSuccessProps {
  onHome: () => void;
  onContinue: () => void;
  /**
   * 投稿者本人が後で状況確認するための viewer_token UUID。
   * 投稿成功時にだけ渡されることを想定。デモモード時は undefined。
   */
  viewerToken?: string;
  /**
   * Token と一緒に localStorage / 表示で参照する案件情報。
   */
  reportInfo?: {
    caseId: string;
    title: string;
    /** 投稿テキスト本体. 「深掘り分析」CTA で why-demo に渡す preset として使う. */
    body?: string;
  };
  stats?: {
    newTotal: number;
    rankingChange?: number;
  };
  /**
   * デモモード（QR トークン無しでアクセス）か。
   * true のとき、完了画面の上に「会社で使うには登録してください」CTA を出す。
   */
  isDemoMode?: boolean;
  /**
   * SA-2: トップLP→Hero「今すぐ30秒で試す」からの本登録への遷移。
   */
  onSignUp?: () => void;
  /**
   * 4M なぜなぜ分析デモ (/why-demo) への遷移. 投稿 body を preset で渡せるよう App.tsx 側で処理.
   */
  onDeepAnalyze?: (problem: string) => void;
  /**
   * 写真を添えたのにアップロードに失敗したか。
   * トーストは見逃されるので、完了画面にも消えない形で残す。
   */
  imageUploadFailed?: boolean;
}

const MY_REPORTS_LS_KEY = 'genbasafeai_my_reports';

function persistMyReport(token: string, caseId: string, title: string) {
  try {
    const existing = localStorage.getItem(MY_REPORTS_LS_KEY);
    const list: Array<{ token: string; caseId: string; title: string; createdAt: string }> =
      existing ? JSON.parse(existing) : [];
    // 重複は弾く（同一トークンで複数保存しない）
    if (list.some((r) => r.token === token)) return;
    list.unshift({ token, caseId, title, createdAt: new Date().toISOString() });
    // 直近 50 件まで保持
    const trimmed = list.slice(0, 50);
    localStorage.setItem(MY_REPORTS_LS_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage が使えない環境（プライベートブラウジング等）は黙って諦める
  }
}

export const WorkerSuccess: React.FC<WorkerSuccessProps> = ({
  onHome,
  onContinue,
  viewerToken,
  reportInfo,
  stats = { newTotal: 13 } as WorkerSuccessProps['stats'],
  isDemoMode = false,
  onSignUp,
  onDeepAnalyze,
  imageUploadFailed = false,
}) => {
  const [copied, setCopied] = useState<'url' | 'id' | null>(null);

  const trackingUrl = viewerToken
    ? `${window.location.origin}/my-report/${viewerToken}`
    : null;

  // localStorage に自動保存（同端末からの再アクセス用）
  useEffect(() => {
    if (viewerToken && reportInfo) {
      persistMyReport(viewerToken, reportInfo.caseId, reportInfo.title);
    }
  }, [viewerToken, reportInfo]);

  const copy = async (text: string, kind: 'url' | 'id') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div className="text-center py-8 animate-fade-in">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-100 rounded-full mb-6 animate-bounce">
        <CheckCircle2 size={40} className="text-emerald-600" />
      </div>
      <h3 className="text-2xl font-bold text-industrial-900 mb-2">送信完了</h3>
      <p className="text-slate-500 mb-6">
        貴重な報告ありがとうございます。
        <br />
        AIが内容を匿名化し、安全活動に役立てます。
      </p>

      {/* 写真の添付だけが失敗したケース。報告は保存済みなので「送信失敗」と誤解させない文言にする。 */}
      {imageUploadFailed && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 mb-6 text-left">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-900 mb-1">
                写真は添付できませんでした
              </p>
              <p className="text-xs text-amber-800 leading-relaxed">
                報告そのものは正常に送信され、管理者に届いています。
                写真だけがアップロードできませんでした（通信状況または設定の問題）。
                写真が重要な場合は、現場責任者に直接お知らせください。
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 深掘り分析への誘導 (送信完了直下の最も目立つ位置). reportInfo.body があれば必ず表示. */}
      {onDeepAnalyze && reportInfo?.body && (
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 mb-6 text-left text-white shadow-lg shadow-blue-500/30">
          <div className="flex items-start gap-2.5 mb-3">
            <span className="text-2xl leading-none">🔍</span>
            <div className="flex-1">
              <h4 className="font-bold text-base mb-1">この報告をさらに深掘り分析</h4>
              <p className="text-xs leading-relaxed text-blue-100">
                人 / 機械 / 材料 / 方法 の <strong>4観点それぞれで 5段なぜなぜ</strong> + 即時/恒久対策を AI が即時生成。無料・登録不要。
              </p>
            </div>
          </div>
          <button
            onClick={() => onDeepAnalyze(reportInfo.body || '')}
            className="w-full inline-flex items-center justify-center gap-2 bg-white text-indigo-700 font-bold py-3 px-4 rounded-xl hover:bg-indigo-50 active:scale-95 transition text-sm"
          >
            なぜなぜ深掘り分析を試す
            <span aria-hidden>→</span>
          </button>
        </div>
      )}

      {/* SA-2: デモ完了時のみ「会社で使うには登録してください」CTA */}
      {isDemoMode && onSignUp && (
        <div className="bg-gradient-to-r from-safety-500 to-yellow-500 rounded-2xl p-5 mb-6 text-left text-white shadow-lg">
          <div className="flex items-start gap-3">
            <div className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-full shrink-0">
              CONGRATS
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-base mb-1.5">30秒の体験、いかがでしたか？</h4>
              <p className="text-xs leading-relaxed text-white/95 mb-3">
                これがあなたの現場で動きます。無料プランから登録すれば、QRコード3個・AI分析10回/月までずっと無料。
              </p>
              <button
                onClick={onSignUp}
                className="w-full bg-white text-orange-600 font-bold py-2.5 rounded-lg text-sm hover:bg-orange-50 transition flex items-center justify-center gap-1"
              >
                無料で始める（カード不要）→
              </button>
            </div>
          </div>
        </div>
      )}

      {/* デモ時のみ: 本番モードでは「管理者の対応経過」が見えることを説明 */}
      {isDemoMode && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 text-left">
          <div className="flex items-start gap-2">
            <CheckCircle2 size={16} className="text-emerald-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-emerald-900 mb-1">本番では「対応経過」も見えます</p>
              <p className="text-xs text-emerald-800 leading-relaxed">
                投稿後に専用URLが発行され、管理者の対応状況（未対応→対応中→対応済み）と実施内容を匿名のまま確認できます。
                「自分の声がどう活かされたか」がわかる仕組みです。
              </p>
            </div>
          </div>
        </div>
      )}

      {viewerToken && trackingUrl && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-6 text-left">
          <div className="flex items-start gap-2 mb-3">
            <LinkIcon size={16} className="text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-900 mb-1">この投稿の対応経過を確認できます</p>
              <p className="text-xs text-amber-800 leading-relaxed">
                下記URLを保存しておくと、管理者の対応状況（未対応→対応中→対応済み）と
                <span className="font-semibold">実施した対策の内容</span>を匿名のまま確認できます。
                <br />
                <span className="font-semibold">他人には共有しないでください。</span>
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="bg-white rounded-lg p-2.5 border border-amber-200">
              <p className="text-[10px] text-slate-500 mb-1 font-medium">確認URL</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-700 truncate flex-1">{trackingUrl}</span>
                <button
                  type="button"
                  onClick={() => copy(trackingUrl, 'url')}
                  className="shrink-0 inline-flex items-center justify-center gap-1 text-xs font-bold px-3 min-h-[44px] min-w-[64px] bg-amber-600 text-white rounded-lg hover:bg-amber-700 active:scale-95 transition"
                  aria-label="URLをコピー"
                >
                  {copied === 'url' ? (
                    <>
                      <Check size={12} />コピー済
                    </>
                  ) : (
                    <>
                      <Copy size={12} />コピー
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="bg-white rounded-lg p-2.5 border border-amber-200">
              <p className="text-[10px] text-slate-500 mb-1 font-medium">報告ID（参考）</p>
              <div className="flex items-center gap-2">
                <code className="text-[11px] text-slate-700 font-mono break-all flex-1">
                  {viewerToken}
                </code>
                <button
                  type="button"
                  onClick={() => copy(viewerToken, 'id')}
                  className="shrink-0 inline-flex items-center justify-center gap-1 text-xs px-3 min-h-[44px] bg-slate-600 text-white rounded-lg hover:bg-slate-700 active:scale-95 transition"
                  aria-label="IDをコピー"
                >
                  {copied === 'id' ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
            </div>
          </div>

          <p className="text-[10px] text-amber-700 mt-2 leading-relaxed">
            ※ このURLはこの端末にも自動保存されます。スマホを変えるなど別端末で確認したい場合はURLを必ず控えてください。
          </p>

          <a
            href={trackingUrl}
            className="mt-3 inline-flex items-center gap-1 text-xs text-amber-800 font-medium hover:underline"
          >
            <ExternalLink size={12} />
            今すぐ状況確認画面を開く
          </a>
        </div>
      )}

      <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left border border-slate-100">
        <p className="text-xs text-slate-600 mb-1">今月のあなたのチーム</p>
        <div className="flex justify-between items-end">
          {/* 直前に「今月の」と書いてあるが、単独で読んでも累計と誤解しないよう揃える */}
          <span className="font-medium text-industrial-900">報告数</span>
          <span className="text-xl font-bold text-safety-600">
            {stats.newTotal}
            <span className="text-xs text-slate-500 ml-1">(+1)</span>
          </span>
        </div>
        {stats.rankingChange && stats.rankingChange > 0 && (
          <div className="mt-2 text-sm text-emerald-600 font-medium">
            ランキングが{stats.rankingChange}位上昇しました!
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-8 text-left">
        <p className="text-sm text-blue-800">
          数日後、あなたの投稿は教育用ケースとしてチームに共有されます。
        </p>
      </div>

      <div className="space-y-3">
        <button
          onClick={onContinue}
          className="w-full py-3 px-4 bg-safety-600 text-white font-bold rounded-xl hover:bg-safety-700 transition-all active:scale-95"
        >
          続けて投稿する
        </button>
        <button
          onClick={onHome}
          className="w-full py-3 px-4 border border-slate-300 text-slate-600 font-medium rounded-xl hover:bg-slate-50 transition"
        >
          ホームに戻る
        </button>
      </div>
    </div>
  );
};
